/**
 * PlayerAssetPreviewCard — right-rail "How it looks in the app" preview for
 * the player-asset add/edit form (avatars, player backgrounds, app music).
 *
 * Bound live to the form: every value arrives through `asset`, already
 * resolved to "local preview while uploading, else the hosted URL".
 *
 * The mockups mirror the iOS Customize Tapping screen and the player
 * (TappingSolutionUI `CustomizeTappingAvatarOptionView`,
 * `CustomizeTappingBackgroundImageOptionView`, `CustomizeTappingGridImageButton`,
 * `CustomizeTappingSelectedBorder`, `PlayerAvatarView`).
 *
 * Theme: like the other PreviewCards, the app mockups keep FIXED app colors
 * (they imitate the mobile app, see themeTokens.css). Only the chrome around
 * them (stage, captions, toggle) follows the CMS theme tokens.
 */
import React, { useEffect, useRef, useState } from "react";
import RailCard, { RailCardHead } from "../../redesign/edit/RailCard";
import { SegmentedControl } from "../../redesign";

/* Fixed app colors — these mimic the mobile app, not the CMS theme. */
export const APP_COLORS = {
  // `tappingBackgroundDark`, the player's default background image.
  playerDefaultBackground: "linear-gradient(180deg, #013C50 0%, #00242F 55%, #001A23 100%)",
  // `LinearGradient.sunset` (sunGlow → sunShade), the selected-tile border.
  selectedBorder: "linear-gradient(180deg, #FFC107, #FF9927)",
  // Blue wash over a music tile image (CustomizeTappingGridImageButton).
  musicOverlay: "linear-gradient(180deg, #65C8DA, #048CB9)",
  // Background tile dimming (Color.black.opacity(0.25)).
  backgroundTileDim: "#00000040",
  sheet: "#FFFFFF",
  sheetText: "#1C1C1E",
  placeholderTile: "#E5E5EA",
  deviceFrame: "#111111",
  onMedia: "#FFFFFF",
};

/* ─── Small building blocks ──────────────────────────────────────────── */

function StageCaption({ children, flush }) {
  return (
    <div
      className="font-condensed font-bold uppercase text-text-muted"
      style={{ fontSize: 10, letterSpacing: "0.1em", marginBottom: flush ? 0 : 8 }}
    >
      {children}
    </div>
  );
}

function EmptyState({ children }) {
  return (
    <div
      data-testid="preview-empty"
      className="text-text-muted border border-dashed border-sand bg-surface text-center"
      style={{ borderRadius: 10, padding: "28px 16px", fontSize: 12.5, lineHeight: 1.5 }}
    >
      {children}
    </div>
  );
}

// `CustomizeTappingSelectedBorder`: 4pt sunset-gradient stroke over the tile.
function SelectedBorder({ radius }) {
  return (
    <span
      aria-hidden
      style={{
        position: "absolute",
        inset: 0,
        borderRadius: radius,
        padding: 3,
        background: APP_COLORS.selectedBorder,
        WebkitMask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
        WebkitMaskComposite: "xor",
        maskComposite: "exclude",
        pointerEvents: "none",
      }}
    />
  );
}

// A slice of the Customize Tapping sheet: section header + a row of tiles.
function CustomizeSheet({ title, children }) {
  return (
    <div
      style={{
        background: APP_COLORS.sheet,
        borderRadius: 14,
        padding: "12px 12px 14px",
        boxShadow: "0 4px 14px #0000001F",
        fontFamily: "Asap, var(--font-sans)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          color: APP_COLORS.sheetText,
          fontSize: 12.5,
          fontWeight: 700,
          marginBottom: 10,
        }}
      >
        <span>{title}</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>{children}</div>
    </div>
  );
}

// The neighbouring, unselected option — gives the row its real context.
function PlaceholderTile({ aspectRatio }) {
  return (
    <div aria-hidden style={{ aspectRatio, borderRadius: 5, background: APP_COLORS.placeholderTile, opacity: 0.8 }} />
  );
}

function TileLabel({ children }) {
  return (
    <span
      style={{
        position: "relative",
        color: APP_COLORS.onMedia,
        fontSize: 13,
        fontWeight: 500,
        textAlign: "center",
        padding: "0 8px",
        lineHeight: 1.2,
        overflowWrap: "anywhere",
      }}
    >
      {children}
    </span>
  );
}

// Tile size ratio from CustomizeTappingLayout.phoneTileSize (158 × 188).
const TILE_RATIO = "158 / 188";
// Music card ratio from CustomizeTappingLayout.musicCardAspectRatio (220 : 134).
const MUSIC_RATIO = "220 / 134";

function AvatarSilhouette() {
  return (
    <svg viewBox="0 0 100 120" width="100%" height="100%" preserveAspectRatio="xMidYMax meet" aria-hidden>
      <circle cx="50" cy="34" r="20" fill="#FFFFFFB3" />
      <path d="M12 120c0-26 17-44 38-44s38 18 38 44z" fill="#FFFFFFB3" />
    </svg>
  );
}

/**
 * Phone-shaped player mock: background full-bleed, avatar anchored above the
 * transport controls (PlayerAvatarView pads the avatar and keeps 48pt below).
 * All colors here are fixed app colors (the mock imitates the app, not the CMS).
 */
function PlayerMock({ background, avatar, placeholderAvatar, orientation = "portrait" }) {
  const landscape = orientation === "landscape";
  return (
    <div
      data-testid="player-mock"
      data-orientation={orientation}
      style={{
        position: "relative",
        width: landscape ? "100%" : 150,
        maxWidth: landscape ? 300 : 150,
        aspectRatio: landscape ? "812 / 375" : "375 / 812",
        margin: "0 auto",
        borderRadius: landscape ? 18 : 22,
        border: `5px solid ${APP_COLORS.deviceFrame}`,
        overflow: "hidden",
        background: APP_COLORS.playerDefaultBackground,
        boxShadow: "0 10px 24px #00000033",
      }}
    >
      {background && (
        <img
          data-testid="player-background"
          src={background}
          alt=""
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
        />
      )}

      {/* Nav row: close chevron + session title placeholder */}
      <div
        aria-hidden
        style={{ position: "absolute", top: 12, left: 10, right: 10, display: "flex", alignItems: "center", gap: 6 }}
      >
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="3">
          <polyline points="6 9 12 15 18 9" />
        </svg>
        <span style={{ height: 4, borderRadius: 2, background: "#FFFFFFB3", flex: 1, maxWidth: 70 }} />
      </div>

      {(avatar || placeholderAvatar) && (
        <div
          style={{
            position: "absolute",
            left: landscape ? "30%" : "8%",
            right: landscape ? "30%" : "8%",
            top: landscape ? "14%" : "22%",
            bottom: landscape ? "22%" : "17%",
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "center",
          }}
        >
          {avatar ? (
            <img
              data-testid="player-avatar"
              src={avatar}
              alt=""
              style={{ width: "100%", height: "100%", objectFit: "contain", objectPosition: "bottom" }}
            />
          ) : (
            <AvatarSilhouette />
          )}
        </div>
      )}

      {/* Transport: progress bar + play button */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          left: 12,
          right: 12,
          bottom: 10,
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span
          style={{
            width: 18,
            height: 18,
            borderRadius: "50%",
            background: "#FFFFFF",
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
          }}
        >
          <svg width="8" height="8" viewBox="0 0 24 24" fill="#013C50">
            <path d="M7 4v16l13-8z" />
          </svg>
        </span>
        <span style={{ flex: 1, height: 3, borderRadius: 2, background: "#FFFFFF4D", position: "relative" }}>
          <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: "35%", background: "#FFFFFF" }} />
        </span>
      </div>
    </div>
  );
}

/* ─── Per-type previews ──────────────────────────────────────────────── */

function AvatarPreview({ asset }) {
  const tileImage = asset.thumbnail || asset.image;
  return (
    <>
      <StageCaption>Customize screen</StageCaption>
      <CustomizeSheet title="Choose your tapping avatar">
        {/* CustomizeTappingAvatarOptionView: the avatar over the current
            background (the default dark one), "No Avatar" text when empty. */}
        <div
          data-testid="avatar-tile"
          style={{
            position: "relative",
            aspectRatio: TILE_RATIO,
            borderRadius: 5,
            overflow: "hidden",
            background: APP_COLORS.playerDefaultBackground,
            display: "grid",
            placeItems: "center",
          }}
        >
          {tileImage ? (
            <img
              src={tileImage}
              alt=""
              style={{
                position: "absolute",
                inset: 10,
                width: "calc(100% - 20px)",
                height: "calc(100% - 20px)",
                objectFit: "contain",
              }}
            />
          ) : (
            <TileLabel>No Avatar</TileLabel>
          )}
          <SelectedBorder radius={5} />
        </div>
        <PlaceholderTile aspectRatio={TILE_RATIO} />
      </CustomizeSheet>

      <div style={{ height: 16 }} />
      <StageCaption>Player</StageCaption>
      <PlayerMock avatar={asset.image} />

      {!asset.image && (
        <p className="text-text-muted text-center" style={{ fontSize: 11.5, margin: "10px 0 0", lineHeight: 1.45 }}>
          Upload an image to see the avatar on the player. Without one, this is the “No Avatar” option.
        </p>
      )}
    </>
  );
}

function BackgroundPreview({ asset }) {
  const hasPortrait = !!asset.image;
  const hasLandscape = !!asset.wideImage;
  const [orientation, setOrientation] = useState("portrait");

  if (!hasPortrait && !hasLandscape) {
    return <EmptyState>Upload an image to see the preview.</EmptyState>;
  }

  // Without both images there is nothing to toggle: show the one that exists.
  const shown = hasPortrait && hasLandscape ? orientation : hasPortrait ? "portrait" : "landscape";
  const tileImage = asset.image || asset.wideImage;
  const name = asset.name?.trim() || "Background name";

  return (
    <>
      <StageCaption>Customize screen</StageCaption>
      <CustomizeSheet title="Choose your tapping background">
        {/* CustomizeTappingBackgroundImageOptionView: image, 25% black, name. */}
        <div
          data-testid="background-tile"
          style={{
            position: "relative",
            aspectRatio: TILE_RATIO,
            borderRadius: 5,
            overflow: "hidden",
            display: "grid",
            placeItems: "center",
          }}
        >
          <img
            src={tileImage}
            alt=""
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
          />
          <span aria-hidden style={{ position: "absolute", inset: 0, background: APP_COLORS.backgroundTileDim }} />
          <TileLabel>{name}</TileLabel>
          <SelectedBorder radius={5} />
        </div>
        <PlaceholderTile aspectRatio={TILE_RATIO} />
      </CustomizeSheet>

      <div style={{ height: 16 }} />
      <div className="flex items-center justify-between" style={{ marginBottom: 8, gap: 8 }}>
        <StageCaption flush>Player</StageCaption>
        {hasPortrait && hasLandscape && (
          <SegmentedControl
            value={orientation}
            onChange={setOrientation}
            options={[
              { value: "portrait", label: "Portrait" },
              { value: "landscape", label: "Landscape" },
            ]}
          />
        )}
      </div>
      <PlayerMock
        orientation={shown}
        background={shown === "landscape" ? asset.wideImage : asset.image}
        placeholderAvatar
      />
    </>
  );
}

const PlayGlyph = (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M7 4v16l13-8z" />
  </svg>
);
const PauseGlyph = (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <rect x="6" y="5" width="4" height="14" />
    <rect x="14" y="5" width="4" height="14" />
  </svg>
);

function MusicPreview({ asset }) {
  const audioRef = useRef(null);
  const playingRef = useRef(false);
  const [playing, setPlaying] = useState(false);

  const setPlayingState = value => {
    playingRef.current = value;
    setPlaying(value);
  };

  // A new file (or a removed one) must never keep playing the old audio.
  useEffect(() => {
    if (playingRef.current && audioRef.current) audioRef.current.pause();
    setPlayingState(false);
  }, [asset.audio]);

  useEffect(
    () => () => {
      if (playingRef.current && audioRef.current) audioRef.current.pause();
    },
    []
  );

  if (!asset.audio && !asset.image) {
    return <EmptyState>Upload an audio file to see the preview.</EmptyState>;
  }

  const toggle = () => {
    const el = audioRef.current;
    if (!el || !asset.audio) return;
    if (playing) {
      el.pause();
      setPlayingState(false);
      return;
    }
    setPlayingState(true);
    const result = el.play();
    if (result && typeof result.catch === "function") result.catch(() => setPlayingState(false));
  };

  const name = asset.name?.trim() || "Music name";

  return (
    <>
      <StageCaption>Customize screen</StageCaption>
      <CustomizeSheet title="Choose background music">
        {/* CustomizeTappingGridImageButton: tile image, 35% blue wash, name. */}
        <div
          data-testid="music-tile"
          style={{
            position: "relative",
            aspectRatio: MUSIC_RATIO,
            borderRadius: 8,
            overflow: "hidden",
            display: "grid",
            placeItems: "center",
            background: asset.image ? "transparent" : APP_COLORS.musicOverlay,
          }}
        >
          {asset.image && (
            <img
              src={asset.image}
              alt=""
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
            />
          )}
          <span
            aria-hidden
            style={{ position: "absolute", inset: 0, background: APP_COLORS.musicOverlay, opacity: 0.35 }}
          />
          <TileLabel>{name}</TileLabel>
          <SelectedBorder radius={5} />
        </div>
        <PlaceholderTile aspectRatio={MUSIC_RATIO} />
      </CustomizeSheet>

      <div className="flex items-center" style={{ gap: 10, marginTop: 14 }}>
        <button
          type="button"
          onClick={toggle}
          disabled={!asset.audio}
          aria-label={playing ? "Pause preview" : "Play preview"}
          className="inline-grid place-items-center border-0 cursor-pointer bg-teal-green-soft text-teal-green-dark hover:bg-teal-green hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ width: 34, height: 34, borderRadius: "50%", flexShrink: 0, transition: "all 140ms ease" }}
        >
          {playing ? PauseGlyph : PlayGlyph}
        </button>
        <span className="text-text-light" style={{ fontSize: 12, lineHeight: 1.4 }}>
          {asset.audio ? "Plays the selected audio file." : "Upload an audio file to play it here."}
        </span>
      </div>
      <audio
        ref={audioRef}
        data-testid="music-audio"
        src={asset.audio || undefined}
        preload="none"
        onEnded={() => setPlayingState(false)}
      />
    </>
  );
}

const PREVIEWS = {
  avatar: AvatarPreview,
  background: BackgroundPreview,
  background_music: MusicPreview,
};

/**
 * @param {object} props
 * @param {object} props.config  the PLAYER_ASSET_TYPES entry
 * @param {object} props.asset   { name, image, thumbnail, wideImage, audio } — URLs already resolved
 */
export default function PlayerAssetPreviewCard({ config, asset }) {
  const Preview = PREVIEWS[config.type];
  if (!Preview) return null;

  return (
    <RailCard
      variant="preview"
      head={({ open, toggle }) => (
        <RailCardHead variant="preview" eyebrow="How it looks in the app" open={open} onToggle={toggle} />
      )}
    >
      <div data-testid={`preview-${config.type}`} className="bg-cream" style={{ padding: 16 }}>
        <Preview asset={asset} />
      </div>
    </RailCard>
  );
}
